import {
  excessAlfa,
  dailyFromCumulative as nqDailyFromCumulative,
} from '@novaquantis/nq-finance';
import type {
  Position,
  RetailAccountContent,
  PerformanceHistoryPoint,
  PerformanceHistoryResponse,
  PerformanceHistoryRange,
  PerformanceCandle,
} from '../types';
import type { ComparisonAccount } from './comparisonTypes';
import { BENCHMARK_LABEL, BENCHMARK_SERIES_ID } from './comparisonTypes';

const isOpenPosition = (p: { qty?: number }) => (p.qty ?? 0) > 0;

const filterOpenPositions = <T extends { qty?: number }>(positions: T[]) =>
  positions.filter(isOpenPosition);

/** Convert a content position into the shared `Position` shape (handles legacy %field). */
export const toPosition = (
  raw: NonNullable<RetailAccountContent['positions']>[number],
): Position => ({
  symbol: raw.symbol,
  qty: raw.qty,
  avgEntryPrice: raw.avgEntryPrice,
  currentPrice: raw.currentPrice,
  marketValue: raw.marketValue,
  unrealizedPL: raw.unrealizedPL,
  unrealizedPLPct:
    raw.unrealizedPLPercentage !== undefined
      ? raw.unrealizedPLPercentage / 100
      : undefined,
  heldSince: raw.heldSince,
  lastSoldAt: raw.lastSoldAt,
});

/** Open positions for a portfolio, in shared `Position` shape. */
export const openPositions = (content?: RetailAccountContent): Position[] =>
  filterOpenPositions(content?.positions ?? []).map(toPosition);

export const investedTotal = (content?: RetailAccountContent): number => {
  if (content?.stockValue !== undefined) return content.stockValue;
  return openPositions(content).reduce((sum, p) => sum + (p.marketValue ?? 0), 0);
};

export const positionWeight = (
  position: Position,
  invested: number,
): number => (invested > 0 ? ((position.marketValue ?? 0) / invested) * 100 : 0);

/** Headline performance metrics used in the side-by-side breakdown. */
export interface PerformanceSummary {
  totalValue?: number;
  invested?: number;
  cash?: number;
  investedPct?: number;
  cashPct?: number;
  todayAmount?: number;
  todayPct?: number;
  lastMonthAmount?: number;
  lastMonthPct?: number;
  sinceStartAmount?: number;
  sinceStartPct?: number;
  alphaToday?: number;
  alphaLastMonth?: number;
  alphaSinceStart?: number;
  positionCount: number;
  unrealizedPL: number;
  unrealizedPLPct?: number;
  /** NYSE regular session open. When explicitly false the daily alpha is the
   * frozen last-session value, so it is suppressed in the UI (shown as a dash). */
  marketOpen?: boolean;
}

const sumUnrealized = (positions: Position[]) => {
  const totalPL = positions.reduce((sum, p) => sum + (p.unrealizedPL ?? 0), 0);
  const costBasis = positions.reduce((sum, p) => {
    if (p.marketValue !== undefined && p.unrealizedPL !== undefined) {
      return sum + (p.marketValue - p.unrealizedPL);
    }
    return sum;
  }, 0);
  return {
    unrealizedPL: totalPL,
    unrealizedPLPct: costBasis > 0 ? totalPL / costBasis : undefined,
  };
};

const normalizePercentRatio = (value?: number) => {
  if (value === undefined || value === null) return undefined;
  return value > 1 || value < -1 ? value / 100 : value;
};

/** Alpha for one period: portfolio return minus SPY return over the same window. */
const periodAlpha = (
  portfolioPct?: number,
  benchmarkReturn?: number,
  fallbackAlfa?: number,
) => {
  const bench = normalizePercentRatio(benchmarkReturn);
  if (portfolioPct !== undefined && bench !== undefined) {
    return excessAlfa(portfolioPct, bench);
  }
  return normalizePercentRatio(fallbackAlfa);
};

/** Notional principal for synthetic SPY dollar amounts in the breakdown table. */
const BENCHMARK_NOTIONAL = 10_000;

const benchmarkPctBetween = (from: number, to: number): number | undefined => {
  if (from <= 0) return undefined;
  return (to - from) / from;
};

/**
 * Geometrically un-compound a single step's return from two cumulative-return
 * percentages: `((1 + cum/100) / (1 + prev/100) - 1) * 100`. The comparison
 * series values are cumulative % since inception, so a plain `cum - prev`
 * subtraction overstates the step's actual return once the base is large. This
 * mirrors the candlestick's prior-close basis (`ComparisonCandlestickChart`)
 * and the ledger's `dailyFromCumulative`, so the daily and candle views agree.
 */
export function dailyFromCumulativePct(cumPct: number, prevCumPct: number): number {
  // Percent-space adapter over the canonical decimal-ratio un-compounding in
  // @novaquantis/nq-finance (single source of the formula).
  return nqDailyFromCumulative(cumPct / 100, prevCumPct / 100) * 100;
}

const findBenchmarkPointAtOrBefore = (
  points: PerformanceHistoryPoint[],
  tsSec: number,
): PerformanceHistoryPoint | undefined => {
  if (points.length === 0) return undefined;
  let idx = 0;
  for (let i = 0; i < points.length; i++) {
    if (points[i]!.timestamp <= tsSec) idx = i;
    else break;
  }
  return points[idx];
};

/** History with the earliest benchmark start across compared portfolios. */
export const pickEarliestBenchmarkHistory = (
  accounts: ComparisonAccount[],
): PerformanceHistoryResponse | undefined => {
  let best: PerformanceHistoryResponse | undefined;
  let earliestTs = Infinity;

  for (const account of accounts) {
    const history = account.history;
    if (!history?.benchmarkAvailable || history.points.length === 0) continue;
    const ts = history.points[0]!.timestamp;
    if (ts < earliestTs) {
      earliestTs = ts;
      best = history;
    }
  }

  return best;
};

const benchmarkPeriodReturnsFromHistory = (
  history: PerformanceHistoryResponse,
): {
  sinceStartPct?: number;
  todayPct?: number;
  lastMonthPct?: number;
} => {
  const points = history.points;
  if (points.length === 0) return {};

  const startBench = points[0]!.benchmark;
  const endBench = points[points.length - 1]!.benchmark;
  const sinceStartPct = benchmarkPctBetween(startBench, endBench);

  let todayPct: number | undefined;
  if (points.length >= 2) {
    const prevBench = points[points.length - 2]!.benchmark;
    todayPct = benchmarkPctBetween(prevBench, endBench);
  }

  const lastTs = points[points.length - 1]!.timestamp;
  const monthAgoTs = lastTs - 30 * 24 * 60 * 60;
  const monthPoint = findBenchmarkPointAtOrBefore(points, monthAgoTs);
  const lastMonthPct = monthPoint
    ? benchmarkPctBetween(monthPoint.benchmark, endBench)
    : undefined;

  return { sinceStartPct, todayPct, lastMonthPct };
};

export const performanceSummary = (
  content?: RetailAccountContent,
): PerformanceSummary => {
  const positions = openPositions(content);
  const invested = investedTotal(content);
  const totalValue = content?.totalValue;
  const cash = content?.cash;
  const investedPct =
    totalValue && totalValue > 0 ? invested / totalValue : undefined;
  const cashPct =
    totalValue && totalValue > 0 && cash !== undefined
      ? cash / totalValue
      : undefined;

  const perf = content?.performance;
  const sinceStartAmount =
    perf?.sinceStart?.amount ?? content?.profitSinceStart;
  const sinceStartPct = normalizePercentRatio(
    perf?.sinceStart?.percent ?? content?.profitPercentage,
  );

  const { unrealizedPL, unrealizedPLPct } = sumUnrealized(positions);

  const alphaSinceStart =
    periodAlpha(
      sinceStartPct,
      perf?.sinceStart?.benchmarkReturn,
      content?.vsBenchmarkSinceStart?.benchmarkAvailable
        ? content.vsBenchmarkSinceStart.relativePct
        : perf?.sinceStart?.alfa,
    );

  return {
    totalValue,
    invested,
    cash,
    investedPct,
    cashPct,
    todayAmount: perf?.today?.amount,
    todayPct: normalizePercentRatio(perf?.today?.percent),
    lastMonthAmount: perf?.lastMonth?.amount,
    lastMonthPct: normalizePercentRatio(perf?.lastMonth?.percent),
    sinceStartAmount,
    sinceStartPct,
    alphaToday: periodAlpha(
      normalizePercentRatio(perf?.today?.percent),
      perf?.today?.benchmarkReturn,
      perf?.today?.alfa,
    ),
    alphaLastMonth: periodAlpha(
      normalizePercentRatio(perf?.lastMonth?.percent),
      perf?.lastMonth?.benchmarkReturn,
      perf?.lastMonth?.alfa,
    ),
    alphaSinceStart,
    positionCount: positions.length,
    unrealizedPL,
    unrealizedPLPct,
    marketOpen: content?.marketOpen,
  };
};

/** Principal at track-record start, derived from current value and since-start return. */
export const deriveInitialPrincipal = (
  content?: RetailAccountContent,
): number | undefined => {
  if (!content?.totalValue || content.totalValue <= 0) return undefined;

  const raw =
    content.performance?.sinceStart?.percent ?? content.profitPercentage;
  if (raw === undefined || raw === null) return undefined;

  const pct = normalizePercentRatio(raw);
  if (pct === undefined || pct <= -1) return undefined;

  return Math.round((content.totalValue / (1 + pct)) * 100) / 100;
};

const periodAmountFromPct = (endValue: number, pct?: number) => {
  if (pct === undefined) return undefined;
  return endValue - endValue / (1 + pct);
};

/** SPY benchmark returns for the performance breakdown row. */
export interface BenchmarkSummary {
  label: string;
  todayAmount?: number;
  todayPct?: number;
  lastMonthAmount?: number;
  lastMonthPct?: number;
  sinceStartAmount?: number;
  sinceStartPct?: number;
}

export const benchmarkSummaryFromAccounts = (
  accounts: ComparisonAccount[],
): BenchmarkSummary | undefined => {
  const history = pickEarliestBenchmarkHistory(accounts);

  let sinceStartPct: number | undefined;
  let todayPct: number | undefined;
  let lastMonthPct: number | undefined;

  if (history) {
    ({ sinceStartPct, todayPct, lastMonthPct } =
      benchmarkPeriodReturnsFromHistory(history));
  } else {
    for (const account of accounts) {
      const perf = account.content?.performance;
      if (!perf) continue;
      const hasBench =
        perf.today?.benchmarkReturn !== undefined ||
        perf.lastMonth?.benchmarkReturn !== undefined ||
        perf.sinceStart?.benchmarkReturn !== undefined;
      if (hasBench) {
        todayPct = normalizePercentRatio(perf.today?.benchmarkReturn);
        lastMonthPct = normalizePercentRatio(perf.lastMonth?.benchmarkReturn);
        sinceStartPct = normalizePercentRatio(perf.sinceStart?.benchmarkReturn);
        break;
      }
    }
  }

  if (sinceStartPct === undefined) return undefined;

  const notionalEnd = BENCHMARK_NOTIONAL * (1 + sinceStartPct);
  const sinceStartAmount = BENCHMARK_NOTIONAL * sinceStartPct;

  return {
    label: BENCHMARK_LABEL,
    todayAmount: periodAmountFromPct(notionalEnd, todayPct),
    todayPct,
    lastMonthAmount: periodAmountFromPct(notionalEnd, lastMonthPct),
    lastMonthPct,
    sinceStartAmount,
    sinceStartPct,
  };
};

/** A single row in the position-diff table: one symbol pivoted across portfolios. */
export interface PositionDiffRow {
  symbol: string;
  /** Per-portfolio cell, keyed by accountId. Undefined = portfolio does not hold. */
  cells: Record<string, PositionDiffCell | undefined>;
  /** Number of portfolios holding this symbol. */
  holdersCount: number;
  /** Largest weight across portfolios (used for default sort). */
  maxWeight: number;
  /** Max weight − min weight (over portfolios that hold it); 0 if only one holder. */
  weightSpread: number;
}

export interface PositionDiffCell {
  weightPct: number;
  marketValue?: number;
  qty?: number;
  unrealizedPL?: number;
  unrealizedPLPct?: number;
  avgEntryPrice?: number;
  currentPrice?: number;
  heldSince?: string;
  lastSoldAt?: string;
}

export const buildPositionDiff = (
  accounts: ComparisonAccount[],
): PositionDiffRow[] => {
  const bySymbol = new Map<string, PositionDiffRow>();

  for (const acc of accounts) {
    const positions = openPositions(acc.content);
    const invested = investedTotal(acc.content);
    for (const p of positions) {
      const cell: PositionDiffCell = {
        weightPct: positionWeight(p, invested),
        marketValue: p.marketValue,
        qty: p.qty,
        unrealizedPL: p.unrealizedPL,
        unrealizedPLPct: p.unrealizedPLPct,
        avgEntryPrice: p.avgEntryPrice,
        currentPrice: p.currentPrice,
        heldSince: p.heldSince,
        lastSoldAt: p.lastSoldAt,
      };
      const existing = bySymbol.get(p.symbol);
      if (existing) {
        existing.cells[acc.accountId] = cell;
        existing.holdersCount += 1;
      } else {
        bySymbol.set(p.symbol, {
          symbol: p.symbol,
          cells: { [acc.accountId]: cell },
          holdersCount: 1,
          maxWeight: 0,
          weightSpread: 0,
        });
      }
    }
  }

  for (const row of bySymbol.values()) {
    const weights = Object.values(row.cells)
      .filter((c): c is PositionDiffCell => c !== undefined)
      .map((c) => c.weightPct);
    row.maxWeight = weights.length > 0 ? Math.max(...weights) : 0;
    row.weightSpread =
      weights.length > 1 ? Math.max(...weights) - Math.min(...weights) : 0;
  }

  return Array.from(bySymbol.values()).sort(
    (a, b) => b.maxWeight - a.maxWeight,
  );
};

/** Top-N gainers / losers for a single portfolio, by unrealized P/L %. */
export interface MoverRow {
  symbol: string;
  unrealizedPL?: number;
  unrealizedPLPct?: number;
  marketValue?: number;
}

export const topMovers = (
  content: RetailAccountContent | undefined,
  n: number,
): { gainers: MoverRow[]; losers: MoverRow[] } => {
  const rows: MoverRow[] = openPositions(content)
    .filter((p) => p.unrealizedPLPct !== undefined || p.unrealizedPL !== undefined)
    .map((p) => ({
      symbol: p.symbol,
      unrealizedPL: p.unrealizedPL,
      unrealizedPLPct: p.unrealizedPLPct,
      marketValue: p.marketValue,
    }));

  const sortKey = (r: MoverRow) =>
    r.unrealizedPLPct !== undefined
      ? r.unrealizedPLPct
      : r.unrealizedPL !== undefined && r.marketValue !== undefined && r.marketValue > 0
        ? r.unrealizedPL / r.marketValue
        : r.unrealizedPL ?? 0;

  const sorted = [...rows].sort((a, b) => sortKey(b) - sortKey(a));
  return {
    gainers: sorted.slice(0, n),
    losers: [...sorted].reverse().slice(0, n),
  };
};

/** Cross-portfolio "max disagreement": symbols held by some but not all, or with large weight spread. */
export interface DisagreementRow {
  symbol: string;
  holdersCount: number;
  totalCount: number;
  weightSpread: number;
  cells: Record<string, PositionDiffCell | undefined>;
}

export const buildDisagreements = (
  diffRows: PositionDiffRow[],
  totalAccounts: number,
  limit = 6,
): DisagreementRow[] => {
  if (totalAccounts < 2) return [];
  const exclusives = diffRows.filter(
    (r) => r.holdersCount < totalAccounts && r.holdersCount > 0,
  );
  const wide = diffRows.filter(
    (r) => r.holdersCount === totalAccounts && r.weightSpread >= 5,
  );
  const ranked = [
    ...exclusives.sort((a, b) => b.maxWeight - a.maxWeight),
    ...wide.sort((a, b) => b.weightSpread - a.weightSpread),
  ].slice(0, limit);

  return ranked.map((r) => ({
    symbol: r.symbol,
    holdersCount: r.holdersCount,
    totalCount: totalAccounts,
    weightSpread: r.weightSpread,
    cells: r.cells,
  }));
};

/** Allocation overlap: shared (held by all), partial (held by some), unique (one) symbol counts. */
export interface AllocationOverlapBreakdown {
  shared: {
    symbol: string;
    weights: Record<string, number>;
    plPcts: Record<string, number | undefined>;
  }[];
  partial: { symbol: string; weights: Record<string, number> }[];
  unique: {
    symbol: string;
    accountId: string;
    weight: number;
    unrealizedPLPct?: number;
  }[];
  totalSymbols: number;
}

export const buildAllocationOverlap = (
  diffRows: PositionDiffRow[],
  accounts: ComparisonAccount[],
): AllocationOverlapBreakdown => {
  const total = accounts.length;
  const shared: AllocationOverlapBreakdown['shared'] = [];
  const partial: AllocationOverlapBreakdown['partial'] = [];
  const unique: AllocationOverlapBreakdown['unique'] = [];

  for (const row of diffRows) {
    const weights = Object.fromEntries(
      Object.entries(row.cells)
        .filter(([, c]) => c !== undefined)
        .map(([id, c]) => [id, (c as PositionDiffCell).weightPct]),
    );
    const plPcts = Object.fromEntries(
      Object.entries(row.cells)
        .filter(([, c]) => c !== undefined)
        .map(([id, c]) => [id, (c as PositionDiffCell).unrealizedPLPct]),
    );
    if (row.holdersCount === total && total > 1) {
      shared.push({ symbol: row.symbol, weights, plPcts });
    } else if (row.holdersCount === 1) {
      const [accountId, cell] = Object.entries(row.cells).find(
        ([, c]) => c !== undefined,
      ) as [string, PositionDiffCell];
      unique.push({
        symbol: row.symbol,
        accountId,
        weight: cell.weightPct,
        unrealizedPLPct: cell.unrealizedPLPct,
      });
    } else {
      partial.push({ symbol: row.symbol, weights });
    }
  }

  return { shared, partial, unique, totalSymbols: diffRows.length };
};

/** Resolve position metadata for tooltips across compared accounts. */
export const lookupPositionMeta = (
  accounts: ComparisonAccount[],
  accountId: string,
  symbol: string,
): PositionDiffCell | undefined => {
  const acc = accounts.find((a) => a.accountId === accountId);
  if (!acc?.content?.positions) return undefined;
  const raw = acc.content.positions.find(
    (p) => p.symbol.toUpperCase() === symbol.toUpperCase(),
  );
  if (!raw || (raw.qty ?? 0) <= 0) return undefined;
  const p = toPosition(raw);
  const invested = investedTotal(acc.content);
  return {
    weightPct: positionWeight(p, invested),
    marketValue: p.marketValue,
    qty: p.qty,
    unrealizedPL: p.unrealizedPL,
    unrealizedPLPct: p.unrealizedPLPct,
    avgEntryPrice: p.avgEntryPrice,
    currentPrice: p.currentPrice,
    heldSince: p.heldSince,
    lastSoldAt: p.lastSoldAt,
  };
};

/** Last known sell date for a symbol across compared accounts (for missing cells). */
export const lookupLastSoldAt = (
  accounts: ComparisonAccount[],
  symbol: string,
): string | undefined => {
  const upper = symbol.toUpperCase();
  let latest: string | undefined;
  for (const acc of accounts) {
    for (const pos of acc.content?.positions ?? []) {
      if (
        pos.symbol.toUpperCase() === upper &&
        pos.lastSoldAt &&
        (!latest || pos.lastSoldAt > latest)
      ) {
        latest = pos.lastSoldAt;
      }
    }
  }
  return latest;
};

/** Build a unified time series for the overlay chart, normalized to % return from start. */
export interface NormalizedSeriesPoint {
  /** Unix ms timestamp. */
  t: number;
  /** Display label from the underlying history points. */
  label: string;
  /** Per-portfolio % return (0 = start), keyed by accountId. */
  values: Record<string, number | undefined>;
}

export const buildNormalizedSeries = (
  accounts: ComparisonAccount[],
): NormalizedSeriesPoint[] => {
  const all = accounts
    .map((a) => ({
      accountId: a.accountId,
      points: a.history?.points ?? [],
    }))
    .filter((s) => s.points.length > 0);
  if (all.length === 0) return [];

  const benchmarkHistory = pickEarliestBenchmarkHistory(accounts);

  const allTimestamps = new Set<number>();
  for (const s of all) {
    for (const p of s.points) allTimestamps.add(p.timestamp);
  }
  if (benchmarkHistory) {
    for (const p of benchmarkHistory.points) {
      allTimestamps.add(p.timestamp);
    }
  }
  const timestamps = Array.from(allTimestamps).sort((a, b) => a - b);

  const seriesByAccount = new Map<string, Map<number, { v: number; label: string }>>();
  for (const s of all) {
    const m = new Map<number, { v: number; label: string }>();
    for (const p of s.points) m.set(p.timestamp, { v: p.portfolio, label: p.label });
    seriesByAccount.set(s.accountId, m);
  }

  const benchmarkByTs = new Map<number, number>();
  let benchmarkStart: number | undefined;
  if (benchmarkHistory) {
    for (const p of benchmarkHistory.points) {
      benchmarkByTs.set(p.timestamp, p.benchmark);
    }
    const first = benchmarkHistory.points[0]?.benchmark;
    benchmarkStart = first && first > 0 ? first : undefined;
  }

  const startByAccount = new Map<string, number | undefined>();
  for (const s of all) {
    const first = s.points[0]?.portfolio;
    startByAccount.set(s.accountId, first && first > 0 ? first : undefined);
  }

  const lastByAccount = new Map<string, number | undefined>();
  let lastBenchmark: number | undefined;

  return timestamps.map((t) => {
    let label = '';
    const values: Record<string, number | undefined> = {};
    for (const s of all) {
      const m = seriesByAccount.get(s.accountId)!;
      const here = m.get(t);
      const start = startByAccount.get(s.accountId);
      if (here) {
        if (!label) label = here.label;
        lastByAccount.set(s.accountId, here.v);
      }
      const last = lastByAccount.get(s.accountId);
      values[s.accountId] =
        start !== undefined && last !== undefined
          ? ((last - start) / start) * 100
          : undefined;
    }

    const benchHere = benchmarkByTs.get(t);
    if (benchHere !== undefined) lastBenchmark = benchHere;
    if (benchmarkStart !== undefined && lastBenchmark !== undefined) {
      values[BENCHMARK_SERIES_ID] =
        ((lastBenchmark - benchmarkStart) / benchmarkStart) * 100;
    }

    return { t, label: label || new Date(t).toLocaleDateString(), values };
  });
};

/**
 * Client-side zoom window for the comparison charts, expressed as a timestamp
 * range (in the same unit as `NormalizedSeriesPoint.t` — Unix seconds). Shared
 * across the Return overlay and Daily P&L charts so both stay in sync.
 */
export interface ChartZoomWindow {
  startT: number;
  endT: number;
}

const rowTimestamp = (row: Record<string, unknown>): number => {
  const raw = row.t;
  return typeof raw === 'number' ? raw : Number(raw);
};

/** Keep only the rows whose `t` falls within the zoom window (all rows when null). */
export const filterRowsToWindow = <T extends Record<string, unknown>>(
  rows: T[],
  zoom: ChartZoomWindow | null | undefined,
): T[] => {
  if (!zoom) return rows;
  const { startT, endT } = zoom;
  return rows.filter((r) => {
    const t = rowTimestamp(r);
    return Number.isFinite(t) && t >= startT && t <= endT;
  });
};

/**
 * Map a timestamp zoom window to `{ startIndex, endIndex }` into a chart's own
 * `rows` array, so a recharts `<Brush>` positions its handles to reflect the
 * shared window — even when the window was set from the other (differently
 * bucketed) chart. Returns the full range when there is no zoom or no match.
 */
export const windowToBrushIndices = (
  rows: Array<Record<string, unknown>>,
  zoom: ChartZoomWindow | null | undefined,
): { startIndex: number; endIndex: number } => {
  const full = { startIndex: 0, endIndex: Math.max(rows.length - 1, 0) };
  if (!zoom || rows.length === 0) return full;
  let startIndex = -1;
  let endIndex = -1;
  for (let i = 0; i < rows.length; i++) {
    const t = rowTimestamp(rows[i]);
    if (Number.isFinite(t) && t >= zoom.startT && t <= zoom.endT) {
      if (startIndex === -1) startIndex = i;
      endIndex = i;
    }
  }
  if (startIndex === -1) return full;
  return { startIndex, endIndex };
};

type NavPoint = { time: string; value: number };
type Ohlc = { time: string; open: number; high: number; low: number; close: number };

/**
 * Aggregate a fine-grained value/NAV series into OHLC candles.
 *
 * Buckets points by `bucketKeyFn` and takes open = first point in the bucket,
 * high = max, low = min, close = last. Pure and immutable: the input array is
 * never mutated and fresh objects are returned. Buckets are emitted in
 * first-seen order, so a chronological input yields chronological candles.
 */
export function aggregateNavToOhlc(
  navPoints: readonly NavPoint[],
  bucketKeyFn: (point: NavPoint, index: number) => string,
): Ohlc[] {
  const order: string[] = [];
  const buckets = new Map<string, NavPoint[]>();

  navPoints.forEach((point, index) => {
    const key = bucketKeyFn(point, index);
    const existing = buckets.get(key);
    if (existing) {
      existing.push(point);
    } else {
      buckets.set(key, [point]);
      order.push(key);
    }
  });

  return order.map((key) => {
    const points = buckets.get(key)!;
    const values = points.map((p) => p.value);
    return {
      time: key,
      open: points[0].value,
      high: Math.max(...values),
      low: Math.min(...values),
      close: points[points.length - 1].value,
    };
  });
}

/** UTC hour bucket key ('YYYY-MM-DDTHH') from a Unix-seconds timestamp. */
const hourBucketKey = (timestampSeconds: number): string =>
  new Date(timestampSeconds * 1000).toISOString().slice(0, 13);

/** UTC calendar-day bucket key ('YYYY-MM-DD') from a Unix-seconds timestamp. */
const dayBucketKey = (timestampSeconds: number): string => {
  const d = new Date(timestampSeconds * 1000);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** ISO-8601 week bucket key ('YYYY-Www') from a Unix-seconds timestamp (UTC). */
const isoWeekBucketKey = (timestampSeconds: number): string => {
  const src = new Date(timestampSeconds * 1000);
  const d = new Date(Date.UTC(src.getUTCFullYear(), src.getUTCMonth(), src.getUTCDate()));
  // Shift to the Thursday of the current ISO week (Mon=0..Sun=6).
  const dayNum = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNum + 3);
  const isoYear = d.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const firstThursdayDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstThursdayDayNum + 3);
  const week = 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * 24 * 3600 * 1000));
  return `${isoYear}-W${String(week).padStart(2, '0')}`;
};

/**
 * Bucket a performance history's `portfolio` value into OHLC candles. The
 * `'1D'` range buckets by hour (UTC); `'1W'` by calendar day (UTC); `'1M'` and
 * `'ALL'` by ISO week. Each candle's `timestamp` and `label` come from the last
 * point in its bucket.
 */
export const buildOHLCCandlesticks = (
  points: PerformanceHistoryPoint[],
  range: PerformanceHistoryRange,
): PerformanceCandle[] => {
  const bucketKeyFor = (p: PerformanceHistoryPoint): string =>
    range === '1D'
      ? hourBucketKey(p.timestamp)
      : range === '1W'
        ? dayBucketKey(p.timestamp)
        : isoWeekBucketKey(p.timestamp);

  // Track the last-seen timestamp/label per bucket (points are in order).
  const lastByBucket = new Map<string, { timestamp: number; label: string }>();
  const navPoints: NavPoint[] = points.map((p) => {
    const key = bucketKeyFor(p);
    lastByBucket.set(key, { timestamp: p.timestamp, label: p.label });
    return { time: key, value: p.portfolio };
  });

  const ohlc = aggregateNavToOhlc(navPoints, (point) => point.time);

  return ohlc.map((candle) => {
    const meta = lastByBucket.get(candle.time)!;
    return {
      timestamp: meta.timestamp,
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
      label: meta.label,
    };
  });
};
